import jsPDF from 'jspdf';
import {autoTable } from 'jspdf-autotable';
import axiosInstance from '../api/axiosConfig'; // Assuming you have this configured
  import { useState } from 'react';
import type { Member } from '../models';
// Dummy showToast function for demonstration. Replace with your actual toast implementation.
const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    console.log(`Toast (${type}): ${message}`);
    // In a real app, you'd use a library like react-toastify or your custom toast component
};

// --- CSV Export Function (Add this if you don't have it) ---
interface Header {
    label: string;
    key: string;
}

const exportToCsv = (headers: Header[], data: any[], filename: string) => {
    if (!data || data.length === 0) {
        showToast('No data to export.', 'info');
        return;
    }

    const csvRows = [];
    const headerRow = headers.map(header => header.label).join(',');
    csvRows.push(headerRow);

    for (const row of data) {
        const values = headers.map(header => {
            const value = row[header.key];
            // Handle commas and newlines within data fields by enclosing them in quotes
            return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',');
        csvRows.push(values);
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) { // Feature detection
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};
// --- End CSV Export Function ---

export const useMemberReportGenerator = () => {
    const [loadingAllMembers, setLoadingAllMembers] = useState(false);

    // Accept reportType as a parameter
    const handleGenerateAllMembersReport = async (reportType: 'pdf' | 'csv') => {
        setLoadingAllMembers(true);
        try {
            const response = await axiosInstance.get<Member[]>('/members');
            const membersData: any = response.data;
            const members = membersData.members;

            if (members.length === 0) {
                showToast('No members found to generate report.', 'info');
                return;
            }

            const headers = [
                { label: 'Member ID', key: '_id' },
                { label: 'First Name', key: 'firstName' },
                { label: 'Last Name', key: 'lastName' },
                { label: 'Email', key: 'email' },
                { label: 'Phone Number', key: 'phoneNumber' },
                { label: 'Address', key: 'address' },
                { label: 'Gender', key: 'gender' },
                { label: 'Date of Birth', key: 'dateOfBirth' },
                { label: 'Membership Date', key: 'membershipDate' },
            ];

            const formattedData = members.map(member => ({
                ...member,
                membershipDate: member.membershipDate ? new Date(member.createdAt).toLocaleDateString() : '',
                dateOfBirth: member.dateOfBirth ? new Date(member.dateOfBirth).toLocaleDateString() : '',
            }));

            if (reportType === 'pdf') {
                const doc = new jsPDF();

                const tableHeaders = headers.map(header => header.label);
                const tableBody = formattedData.map(member =>
                    headers.map(header => (member as any)[header.key])
                );

                autoTable(doc, {
                    head: [tableHeaders],
                    body: tableBody,
                    startY: 20,
                    theme: 'striped',
                    headStyles: { fillColor: [22, 160, 133] },
                    didDrawPage: function (data: any) {
                        doc.setFontSize(16);
                        doc.setTextColor(40);
                        doc.text('All Members Report', data.settings.margin.left, 10);

                        let str = 'Page ' + (doc as any).internal.getNumberOfPages();
                        doc.setFontSize(10);
                        doc.text(str, doc.internal.pageSize.width - data.settings.margin.right, doc.internal.pageSize.height - 10, { align: 'right' });
                    }
                });

                doc.save('all_members_report.pdf');
                showToast('All members report (PDF) generated successfully!', 'success');
            } else if (reportType === 'csv') {
                exportToCsv(headers, formattedData, 'all_members_report');
                showToast('All members report (CSV) generated successfully!', 'success');
            } else {
                showToast('Invalid report type specified.', 'error');
            }

        } catch (error) {
            console.error('Failed to generate all members report:', error);
            showToast('Failed to generate all members report. Please try again.', 'error');
        } finally {
            setLoadingAllMembers(false);
        }
    };

    return { handleGenerateAllMembersReport, loadingAllMembers, setLoadingAllMembers };
};
